import { Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { ShippingService } from '../../../shared/services/shipping.service';
import {
  focusFirstDialogElement,
  getDialogExitDuration,
  handleDialogKeydown,
} from '../../../shared/utils/dialog-accessibility.util';

@Component({
  selector: 'app-product-pricing',
  standalone: false,
  templateUrl: './product-pricing.component.html',
  styleUrl: './product-pricing.component.scss',
})
export class ProductPricingComponent implements OnDestroy {
  constructor(public readonly shippingService: ShippingService) {}

  @Input() priceWithTvaNoDiscount: number = 0;
  @Input() finalPriceTTC: number = 0;
  @Input() priceHTTotal: number = 0;
  @Input() deliveryPrice: number = 0;
  @Input() deliveryDays: string | null = null;
  @Input() discountRate: number = 0;
  @Input() originalPriceTTC: number = 0;
  @Input() discountAmount: number = 0;
  @Input() tvaRate: number = 0; // VAT rate from database

  // VAT toggle state
  showPriceWithVAT: boolean = false;
  
  // Popup states
  showShippingPopup: boolean = false;
  showDeliveryPopup: boolean = false;
  isShippingPopupClosing = false;
  isDeliveryPopupClosing = false;

  @ViewChild('shippingDialog') private shippingDialog?: ElementRef<HTMLElement>;
  @ViewChild('deliveryDialog') private deliveryDialog?: ElementRef<HTMLElement>;

  private shippingCloseTimer?: ReturnType<typeof setTimeout>;
  private deliveryCloseTimer?: ReturnType<typeof setTimeout>;
  private shippingTrigger?: HTMLElement;
  private deliveryTrigger?: HTMLElement;
  private previousBodyOverflow: string | null = null;

  toggleVAT(): void {
    this.showPriceWithVAT = !this.showPriceWithVAT;
  }

  getCurrentPrice(): number {
    return this.showPriceWithVAT ? this.finalPriceTTC : this.priceHTTotal;
  }

  getOriginalPrice(): number {
    if (this.showPriceWithVAT) {
      return this.originalPriceTTC;
    } else {
      // Calculate original price without VAT using the database tvaRate
      return this.originalPriceTTC / (1 + this.tvaRate / 100);
    }
  }

  getSavingsAmount(): number {
    if (this.showPriceWithVAT) {
      return this.originalPriceTTC - this.finalPriceTTC;
    } else {
      const originalPriceHT = this.originalPriceTTC / (1 + this.tvaRate / 100);
      return originalPriceHT - this.priceHTTotal;
    }
  }

  getDeliveryTimeText(): string {
    if (this.deliveryDays && this.deliveryDays.trim()) {
      // Check if it's a range (contains dash) or single number
      if (this.deliveryDays.includes('-')) {
        return `Czas dostawy: około ${this.deliveryDays} dni roboczych`;
      } else {
        const days = parseInt(this.deliveryDays);
        return `Czas dostawy: około ${days} ${days === 1 ? 'dzień roboczy' : 'dni roboczych'}`;
      }
    }
    return 'Czas dostawy: około 2–4 dni roboczych';
  }

  getVATAmount(): number {
    return this.finalPriceTTC - this.priceHTTotal;
  }

  getActualVATRate(): number {
    return this.tvaRate; // Use the VAT rate from database
  }

  getVATRate(): number {
    return this.tvaRate; // Use the VAT rate from database
  }

  // Popup methods
  private updateBodyScroll(): void {
    if (typeof document !== 'undefined' && document.body) {
      if (this.showShippingPopup || this.showDeliveryPopup) {
        if (this.previousBodyOverflow === null) {
          this.previousBodyOverflow = document.body.style.overflow;
        }
        document.body.style.overflow = 'hidden';
      } else if (this.previousBodyOverflow !== null) {
        document.body.style.overflow = this.previousBodyOverflow;
        this.previousBodyOverflow = null;
      }
    }
  }

  openShippingPopup(trigger: HTMLElement): void {
    this.clearCloseTimer('shipping');
    this.shippingTrigger = trigger;
    this.isShippingPopupClosing = false;
    this.showShippingPopup = true;
    this.updateBodyScroll();
    setTimeout(() => {
      if (this.showShippingPopup) {
        const dialog = this.shippingDialog?.nativeElement;
        if (dialog) focusFirstDialogElement(dialog);
      }
    });
  }

  closeShippingPopup(): void {
    if (!this.showShippingPopup || this.isShippingPopupClosing) return;
    this.isShippingPopupClosing = true;
    this.shippingCloseTimer = setTimeout(() => {
      this.showShippingPopup = false;
      this.isShippingPopupClosing = false;
      this.shippingCloseTimer = undefined;
      this.updateBodyScroll();
      this.shippingTrigger?.focus();
    }, getDialogExitDuration());
  }

  openDeliveryPopup(trigger: HTMLElement): void {
    this.clearCloseTimer('delivery');
    this.deliveryTrigger = trigger;
    this.isDeliveryPopupClosing = false;
    this.showDeliveryPopup = true;
    this.updateBodyScroll();
    setTimeout(() => {
      if (this.showDeliveryPopup) {
        const dialog = this.deliveryDialog?.nativeElement;
        if (dialog) focusFirstDialogElement(dialog);
      }
    });
  }

  closeDeliveryPopup(): void {
    if (!this.showDeliveryPopup || this.isDeliveryPopupClosing) return;
    this.isDeliveryPopupClosing = true;
    this.deliveryCloseTimer = setTimeout(() => {
      this.showDeliveryPopup = false;
      this.isDeliveryPopupClosing = false;
      this.deliveryCloseTimer = undefined;
      this.updateBodyScroll();
      this.deliveryTrigger?.focus();
    }, getDialogExitDuration());
  }

  onPopupKeydown(event: KeyboardEvent, popup: 'shipping' | 'delivery'): void {
    const dialog = popup === 'shipping'
      ? this.shippingDialog?.nativeElement
      : this.deliveryDialog?.nativeElement;
    if (!dialog) return;

    handleDialogKeydown(event, dialog, () => {
      if (popup === 'shipping') this.closeShippingPopup();
      else this.closeDeliveryPopup();
    });
  }

  private clearCloseTimer(popup: 'shipping' | 'delivery'): void {
    const timer = popup === 'shipping' ? this.shippingCloseTimer : this.deliveryCloseTimer;
    if (timer) clearTimeout(timer);
    if (popup === 'shipping') this.shippingCloseTimer = undefined;
    else this.deliveryCloseTimer = undefined;
  }

  ngOnDestroy(): void {
    this.clearCloseTimer('shipping');
    this.clearCloseTimer('delivery');
    if (typeof document !== 'undefined' && document.body) {
      document.body.style.overflow = this.previousBodyOverflow ?? '';
    }
  }
}
